import type { AdminResourceItemDef } from "./resource-catalog.types";

export const ADMIN_RESOURCE_CATALOG_SYSTEM_CORE: readonly AdminResourceItemDef[] = [
  // ── System ──────────────────────────────────────────────────────────────────
  {
    id: "vault",
    name: "Vault",
    description: "Secret and credential management",
    category: "System",
    actions: [
      {
        id: "view",
        name: "View Vault",
        description: "View secrets (metadata only)",
        unserved:
          "Nothing reads this path. The vault routes guard the item root `admin:vault` and " +
          "separate read from write through the RWX bitmask; only the `pool` block below is " +
          "addressed at its own path."
      },
      {
        id: "manage",
        name: "Manage Vault",
        description: "Add/modify secrets",
        unserved: "Nothing reads this path. Writing is `admin:vault` with the write bit."
      }
    ],
    blocks: [
      {
        id: "pool",
        name: "Credential Pool",
        description: "Shared credential pool management",
        actions: [
          { id: "allocate", name: "Allocate Slots", description: "Allocate to organizations" },
          { id: "revoke", name: "Revoke Slots", description: "Revoke from organizations" }
        ]
      }
    ]
  },
  {
    id: "database",
    name: "Database",
    description: "Database management, migrations, and SQL query runner",
    category: "System",
    actions: [
      {
        id: "view-migrations",
        name: "View Migrations",
        description: "View Prisma migration history"
      },
      { id: "deploy-migrations", name: "Deploy Migrations", description: "Run pending migrations" },
      { id: "execute-sql", name: "Execute SQL", description: "Run SQL queries" }
    ]
  },
  {
    id: "redis-cache",
    name: "Redis Cache",
    description: "Redis cache inspection and management",
    category: "System",
    actions: [
      { id: "list", name: "List Keys", description: "View cache keys" },
      { id: "flush", name: "Flush Cache", description: "Delete all keys (dangerous)" }
    ]
  },
  {
    id: "typesense",
    name: "Typesense",
    description: "Search index management",
    category: "System",
    blocks: [
      {
        id: "messages",
        name: "Messages Index",
        description: "Chat messages search index",
        actions: [
          { id: "backfill", name: "Backfill Messages", description: "Trigger messages backfill" }
        ]
      },
      {
        id: "tools",
        name: "Tools Index",
        description: "Tools/plugins search index",
        actions: [
          { id: "backfill", name: "Backfill Tools", description: "Trigger tools backfill" },
          { id: "nuke", name: "Nuke Collection", description: "Delete and rebuild index" }
        ]
      },
      {
        id: "pipedream",
        name: "Pipedream Categories",
        description: "Pipedream category sync",
        actions: [
          { id: "backfill", name: "Backfill Categories", description: "Fetch from Pipedream API" }
        ]
      }
    ]
  },
  {
    id: "plugins",
    name: "Plugins",
    description: "Plugin management",
    category: "System",
    actions: [
      { id: "stats", name: "View Stats", description: "View plugin statistics" },
      { id: "health", name: "Health Check", description: "Run health checks" },
      { id: "archive", name: "Archive Plugins", description: "Archive deprecated plugins" },
      { id: "delete", name: "Delete Plugins", description: "Permanently delete plugins" }
    ]
  },
  {
    id: "feature-flags",
    name: "Feature Flags",
    description: "Feature flag management and per-org access control",
    category: "System",
    actions: [
      {
        id: "toggle-global",
        name: "Toggle Global Defaults",
        description: "Enable/disable features globally"
      },
      {
        id: "manage-overrides",
        name: "Manage Organization Overrides",
        description: "Whitelist/blacklist specific organizations"
      }
    ],
    blocks: [
      {
        id: "flags-list",
        name: "Feature Flags",
        description: "All available feature flags",
        actions: [
          { id: "view", name: "View Flags", description: "View all feature flags" },
          { id: "edit", name: "Edit Flags", description: "Modify flag settings" }
        ]
      },
      {
        id: "overrides",
        name: "Organization Overrides",
        description: "Per-org access control",
        actions: [
          { id: "add", name: "Add Override", description: "Whitelist/blacklist organization" },
          { id: "remove", name: "Remove Override", description: "Remove organization override" }
        ]
      }
    ]
  },
  {
    id: "system-prompts",
    name: "System Prompts",
    description: "Self-hosted global prompts with version publishing and rollback",
    category: "System",
    actions: [
      {
        id: "publish",
        name: "Publish Version",
        description: "Promote or roll back a prompt version to production"
      }
    ],
    blocks: [
      {
        id: "prompts-list",
        name: "System Prompts",
        description: "All self-hosted system prompts",
        actions: [
          { id: "view", name: "View Prompts", description: "View prompts and version history" },
          { id: "edit", name: "Edit Prompts", description: "Create new prompt versions" }
        ]
      }
    ]
  },
  {
    id: "shared-workspaces",
    name: "Shared Workspaces",
    description: "Workspaces shared across all organizations (read+write for every org)",
    category: "System",
    actions: [
      { id: "view", name: "View Shared Workspaces", description: "List shared workspaces" },
      { id: "create", name: "Create Shared Workspaces", description: "Add a shared workspace" },
      { id: "edit", name: "Rename Shared Workspaces", description: "Rename a shared workspace" },
      { id: "delete", name: "Delete Shared Workspaces", description: "Remove a shared workspace" }
    ]
  },
  {
    id: "providers",
    name: "Providers",
    description: "External service provider management (Twilio subaccounts, phone requests)",
    category: "System",
    blocks: [
      {
        id: "twilio-subaccounts",
        name: "Twilio Subaccounts",
        description: "Twilio subaccount management",
        unserved:
          "Nothing reads this block or either action below. The subaccount routes guard the " +
          "item root `admin:providers`; its siblings `phone-number-requests` and " +
          "`phone-number-drift` ARE guarded at their own paths, which is what makes this a gap.",
        actions: [
          { id: "list", name: "List Subaccounts", description: "View all Twilio subaccounts" },
          {
            id: "close-orphaned",
            name: "Close Orphaned Subaccounts",
            description: "Clean up subaccounts not linked to any organization"
          }
        ]
      },
      {
        id: "phone-number-requests",
        name: "Phone Number Requests",
        description: "Review and approve phone number requests",
        actions: [
          { id: "list", name: "View Requests", description: "View all phone number requests" },
          { id: "approve", name: "Approve Request", description: "Approve phone number request" },
          { id: "reject", name: "Reject Request", description: "Reject with reason" }
        ]
      },
      {
        id: "phone-number-drift",
        name: "Phone Number Drift",
        description:
          "Compare each stored phone number against what Twilio reports for its SID, and re-point a drifted row",
        actions: [
          {
            id: "audit",
            name: "Audit Numbers",
            description: "Read-only sweep comparing PhoneNumber.number with Twilio"
          },
          {
            id: "reconcile",
            name: "Reconcile Number",
            description:
              "Overwrite a drifted row's number with the value Twilio reports for its SID"
          }
        ]
      },
      {
        id: "clerk",
        name: "Clerk",
        description: "Clerk authentication sync status",
        unserved:
          "Nothing reads this block or the action below. The Clerk sync-status route guards " +
          "`admin:providers`.",
        actions: [
          {
            id: "sync-status",
            name: "View Sync Status",
            description: "Check org and user sync status"
          }
        ]
      }
    ]
  }
] as const;
